<script setup lang="ts">
import DuplicationConfirmModal from "@/components/RoleAssignment/DuplicationConfirmModal.vue"
import getRoleAssignAccountsResponse from "@/types/responses/getRoleAssignAccountsResponse"
import getRoleAssignAccountResponseType from "@/types/responses/getRoleAssignedAccount"
import PersonInCharge from "@/components/RoleAssignment/PersonInCharge.vue"
import roleAssignAccountType from "@/types/responses/roleAssignAccount"
import createOrUpdateRole from "@/types/requests/createOrUpdateRole"
import UserSearch from "@/components/RoleAssignment/UserSearch.vue"
import getDepartmentResponse from "@/types/responses/getDepartment"
import RoleSelect from "@/components/RoleAssignment/RoleSelect.vue"
import organizationType from "@/types/requests/organizationType"
import { scrollToErrorComponent } from "@/services/validators"
import { scrollTop, scrollToTop } from "@/services/functions"
import getOfficesResponse from "@/types/responses/getOffices"
import { DEPARTMENT_OFFICE_TYPE } from "@/services/consts"
import { ref, Ref, onMounted, watch } from "vue"
import Api from "@/services/api/ApiServiceFabrick"
import getRoles from "@/types/responses/getRoles"
import getRole from "@/types/responses/getRole"
import accountType from "@/types/account"
import i18n from "@/lang/i18n"

document.title = i18n.global.t('pages.roleAssignment.title')

const props = defineProps<{
  showFlash: (message: string, type: string, fadeout: boolean) => { value: any }
  switchContentLoading: (bool: boolean) => { value: any }
}>()

type searchItemsType = {
  organization: {
    id: number,
    name: string,
    type: string
  } | null,
  userName: string
}

type requestIdsType = {
  assigns: (roleAssignAccountType | accountType)[],
  unassigns: (roleAssignAccountType | accountType)[]
}

const roles: Ref<getRole[]> = ref([])
const alreadySelectedRoleId: Ref<number | null> = ref(null)
const searchItems: Ref<searchItemsType> = ref({
  organization: null,
  userName: ''
})
const assigns: Ref<(roleAssignAccountType | accountType)[]> = ref([])
const requestIds: Ref<requestIdsType> = ref({
  assigns: [],
  unassigns: []
})
const displayAssigns: Ref<roleAssignAccountType[]> = ref([])
const selectableAccounts: Ref<roleAssignAccountType[]> = ref([])
const departments: Ref<getDepartmentResponse[]> = ref([])
const organizations: Ref<organizationType[]> = ref([])
const isCreateOrUpdating: Ref<boolean> = ref(false)
const inforModalStatus: Ref<boolean> = ref(false)
const isConfirmed: Ref<boolean> = ref(false)
const windowInnerWidth: Ref<number> = ref(window.innerWidth)
const loading: Ref<boolean> = ref(false)
const filterValidationErrors: Ref<{
  organization: string[],
  roleType: string[],
  userSearchOrganization: string[],
}> = ref({
  organization: [],
  roleType: [],
  userSearchOrganization: []
})
const selectedOrganizationAndRole: Ref<{
  organization: organizationType | null,
  role: getRole | null
}> = ref({
  organization: null,
  role: null
})
const isSuccessed: Ref<boolean> = ref(false)
const alreadyAssignedAccountIds: Ref<number[]> = ref([])

onMounted(() => {
  scrollToTop()
  requestApis()
  getOrganizationOffices()
})

async function requestApis() {
  props.switchContentLoading(true)
  await getAllRoles()
  props.switchContentLoading(false)
  selectedOrganizationAndRole.value.role = alreadySelectedRole()
}

async function getOrganizationOffices() {
  await getOffices()
  getDepartments()
}

async function getAllRoles() {
  await Api().getRoles().then((data: getRoles) => {
    roles.value = data.data
  }).catch((error) => {
    errorHandle(error.response)
  })
}

async function getAccounts() {
  await Promise.all([
    getAllAccounts(),
    getRoleAssignedAccounts()
  ])
}

async function getAllAccounts() {
  await Api().getRoleAssignAccounts(makeGetAccountsRequestParams()).then((data: getRoleAssignAccountsResponse) => {
    selectableAccounts.value = data.data
  }).catch((error) => {
    errorHandle(error.response)
  })
}

async function getRoleAssignedAccounts() {
  if (!selectedOrganizationAndRole.value.role) {
    return
  }
  await Api().getRoleAssignedAccounts(selectedOrganizationAndRole.value.role.id, makeGetAssignedAccountsRequestParams()).then((data: getRoleAssignAccountResponseType[]) => {
    alreadyAssignedAccountIds.value = data.map((account) => account.id)
  }).catch((error) => {
    errorHandle(error.response)
  })
}

async function getOffices() {
  Api().getOffices().then((data: getOfficesResponse[]) => {
    data.forEach((office: getOfficesResponse) => {
      organizations.value.push({
        id: office.id,
        name: office.company_name ?? office.operation_office_name,
        type: office.type
      })
    })
  })
}

async function getDepartments() {
  await Api().autoCompleteDepartments(undefined)
    .then((data: getDepartmentResponse[]) => {
      departments.value = data
      data.forEach((department: getDepartmentResponse) => {
        organizations.value.push({
          id: department.id,
          name: department.name,
          type: DEPARTMENT_OFFICE_TYPE
        })
      })
    })
}

function makeGetAccountsRequestParams() {
  if (searchItems.value.organization?.type == DEPARTMENT_OFFICE_TYPE) {
    return {
      department_id: searchItems.value.organization?.id,
      account_name: searchItems.value.userName
    }
  }
  return {
    type_class: searchItems.value.organization?.type,
    officeable_id: searchItems.value.organization?.id,
    account_name: searchItems.value.userName
  }
}

function makeGetAssignedAccountsRequestParams() {
  if (searchItems.value.organization?.type == DEPARTMENT_OFFICE_TYPE) {
    return {
      department_id: searchItems.value.organization?.id,
      account_name: searchItems.value.userName
    }
  }
  return {
    officeable_type: searchItems.value.organization?.type,
    officeable_id: searchItems.value.organization?.id,
    account_name: searchItems.value.userName
  }
}

function alreadySelectedRole() {
  if (!alreadySelectedRoleId.value) return null
  return roles.value.find((role) => role.id === alreadySelectedRoleId.value) ?? null
}

async function filter() {
  if (!validationSearchCanditions()) {
    return
  }
  refreshAccounts()
  loading.value = true
  await getAccounts()
  if (alreadyAssignedAccountIds.value.length) {
    alreadyAssignedAccountIds.value.forEach((id: number) => {
      let index = selectableAccounts.value.findIndex((account) => account.id === id)
      if (index !== -1 && selectableAccounts.value[index]) {
        assigns.value.push(selectableAccounts.value[index])
      }
    })
  }
  loading.value = false
  displayAssigns.value = JSON.parse(JSON.stringify(assigns.value))
}

function refreshValidationMessages() {
  filterValidationErrors.value.organization = []
  filterValidationErrors.value.roleType = []
  filterValidationErrors.value.userSearchOrganization = []
}

function validationSearchCanditions() {
  refreshValidationMessages()
  let isValid = true

  if (!selectedOrganizationAndRole.value.organization) {
    filterValidationErrors.value.organization.push(i18n.global.t('pages.roleAssignment.validations.organization.required'))
    isValid = false
  }

  if (!selectedOrganizationAndRole.value.role) {
    filterValidationErrors.value.roleType.push(i18n.global.t('pages.roleAssignment.validations.roleType.required'))
    isValid = false
  }
  if (!searchItems.value.organization) {
    filterValidationErrors.value.userSearchOrganization.push(i18n.global.t('pages.roleAssignment.validations.userSearch.orzanization.required'))
    isValid = false
  }
  if (!isValid) {
    scrollToErrorComponent()
  }
  return isValid
}

function createOrUpdate() {
  refreshValidationMessages()
  if (!isConfirmed.value && isDuplicationOffice()) return inforModalStatus.value = true
  isCreateOrUpdating.value = true
  const requestParams: createOrUpdateRole = {
    assigns: requestIds.value.assigns.map((assign: (roleAssignAccountType | accountType)) => {
      return assign.id
    }),
    unassigns: requestIds.value.unassigns.map((unassign: (roleAssignAccountType | accountType)) => {
      return unassign.id
    })
  }
  if (!selectedOrganizationAndRole.value.role) return
  Api().createOrUpdateRole(selectedOrganizationAndRole.value.role.id, requestParams).then(() => {
    success()
  }).catch((error) => {
    errorHandle(error.response)
  })
}

function success() {
  isSuccessed.value = true
  isConfirmed.value = false
  isCreateOrUpdating.value = false
  props.showFlash(i18n.global.t('pages.common.messages.success'), "success", true)
  scrollTop('flash-message-component')
  if (selectedOrganizationAndRole.value.role) {
    alreadySelectedRoleId.value = selectedOrganizationAndRole.value.role.id
  }
  requestApis()
  getRoleAssignedAccounts()
}

function errorHandle(error: any) {
  switch(error.status){
    case 403:
      props.showFlash(i18n.global.t('pages.common.messages.notEditAuthority'), "error", true)
      location.reload()
      break
    default:
      break
  }
}

function isDuplicationOffice() {
  let isDuplication = false

  requestIds.value.assigns.forEach((assignAccount) => {
    if (isDuplication) return
    if (assignAccount?.roles) {
      assignAccount.roles.forEach((role) => {
        if (
          selectedOrganizationAndRole.value.role &&
          selectedOrganizationAndRole.value.role.organization_type == role.organization_type &&
          selectedOrganizationAndRole.value.role.organization_id == role.organization_id &&
          role.id != selectedOrganizationAndRole.value.role.id
        ) return isDuplication = true
      })
    }
  })
  return isDuplication
}

function duplicateModalExec() {
  isConfirmed.value = true
  inforModalStatus.value = false
  createOrUpdate()
}

function changeDisplayAssigns(accounts: roleAssignAccountType[], isAssign: boolean) {
  if (isAssign) {
    displayAssigns.value = displayAssigns.value.concat(accounts)
    assigns.value = assigns.value.concat(accounts)
  } else {
    accounts.forEach((account) => {
      let index = displayAssigns.value.findIndex((displayAccount) => displayAccount.id === account.id)
      displayAssigns.value.splice(index, 1)
      assigns.value.splice(index, 1)
    })
  }
}

function handleResize() {
  windowInnerWidth.value = window.innerWidth;
}

function refreshAccounts() {
  displayAssigns.value = []
  selectableAccounts.value = []
  assigns.value = []
  requestIds.value.assigns = []
  requestIds.value.unassigns = []
}

watch(() => selectedOrganizationAndRole.value.role, (after, before) => {
  // 保存成功時に検索条件をリセットされないようにする
  if (!isSuccessed.value && after != before && alreadySelectedRoleId.value != after?.id) {
    refreshAccounts()
    searchItems.value = {
      organization: null,
      userName: ''
    }
  }
  isSuccessed.value = false
}, { deep: true })

window.addEventListener('resize', handleResize);
</script>
<template>
  <div class="d-flex" style="min-height: 45vw;">
    <div class="content p-4 w-100" style="min-width: 75%;">
      <div class="d-flex justify-content-between mb-1">
        <h4 class="card-title mt-0">{{ i18n.global.t('pages.roleAssignment.title') }}</h4>
      </div>
      <div class="w-100">
        <RoleSelect
          v-model="selectedOrganizationAndRole"
          :organizations="organizations"
          :roles="roles"
          :windowInnerWidth="windowInnerWidth"
          :errorMessages="filterValidationErrors"
        />
        <UserSearch
          class="mt-6"
          v-model="searchItems"
          :selectedRole="selectedOrganizationAndRole.role"
          :windowInnerWidth="windowInnerWidth"
          :filter="filter"
          :organizations="organizations"
          :errorMessages="filterValidationErrors"
          :loading="loading"
        />
      </div>
      <div class="w-100 mt-6">
        <PersonInCharge
          :windowInnerWidth="windowInnerWidth"
          :accounts="selectableAccounts"
          :displayAssigns="displayAssigns"
          :alreadyAssignedAccountIds="alreadyAssignedAccountIds"
          :selectedRole="selectedOrganizationAndRole.role"
          :organizations="organizations"
          :changeDisplayAssigns="changeDisplayAssigns"
          v-model="requestIds"
          :loading="loading"
        />
      </div>
      <div
        class="d-flex justify-end mt-6"
      >
        <v-btn
          class="exec-btn"
          @click="createOrUpdate"
          :loading="isCreateOrUpdating"
          :style="{
            backgroundColor : selectedOrganizationAndRole.role ? '' : '#B3B8BB'
          }"
          :disabled="!selectedOrganizationAndRole.role"
        >
          {{ i18n.global.t('pages.common.button.conservation') }}
        </v-btn>
      </div>
    </div>
  </div>
  <DuplicationConfirmModal
    v-model="inforModalStatus"
    :duplicateModalExec="duplicateModalExec"
  />
</template>
